import { BoardQuery, LegStatus, QueryFilter } from '@api/Trinium/types';
import { useMemo } from 'react';
import { useFetchDispatchLegsByJobIdQuery, useFetchDispatchLegsQuery } from '@api/Trinium/Endpoints/Dispatch/slice';
import { createSelector } from '@reduxjs/toolkit';
import { dispatchFilterMaps } from '../Components/Filters/dispatchFilterMaps';
import { operatorMaps } from '../Components/Filters/operatorMaps';
import { useAppSelector } from '@reduxStore';
import { DispatchLeg, sortDispatchLegs, sortJobLegs } from '@api/Trinium/Endpoints/Dispatch/types';
import { getEnumValueByKey } from '@/Infrastructure/Utilities';
import { useSearchParams } from 'react-router';

/**
 * Return shape of useDispatchRows
 */
interface DispatchRowsState {
	/** Current view state of the Dispatch Grid */
	mode: 'consignment' | 'job' | 'dispatch';
	/** Dispatch Leg collection being displayed in the Dispatch Grid */
	dispatchLegs: DispatchLeg[];
	/** Flag indicating an outbound API request is retrieving data */
	isLoading: boolean;
}

/**
 * Hook instance for determining the correct Dispatch Legs to display based on the route the application is at.
 * When Query Parameter job is present than a filtered Job list will be displayed, which can be further filtered by the consignment Query Param.
 * Otherwise, the Dispatch Legs are displayed by the current Board Query should one be present in the route.
 * The selected Board Query will persist to the Job filtered Legs as well, displaying the same column structure.
 */
export default function useDispatchRows(currentQuery: BoardQuery): DispatchRowsState {
	/** Use the search parameters of the current location to determine the dispatch grid mode */
	const [searchParameters] = useSearchParams();

	const jobId = useMemo(() => searchParameters.get('job') ?? undefined, [searchParameters]);
	const csnId = useMemo(() => searchParameters.get('csn') ?? undefined, [searchParameters]);
	const { selectedTerminal } = useAppSelector((state) => state.applicationState);

	/** Local RTK Selector to filter the Dispatch Legs down by the Current Board Query */
	const selectFilteredLegs = useMemo(() => {
		const noLegs: DispatchLeg[] = [];

		return createSelector(
			(legs: DispatchLeg[] | null | undefined) => legs,
			(_: DispatchLeg[] | null | undefined, filters: QueryFilter[] | undefined) => filters,
			(legs, filters) => {
				if (!legs?.length) return noLegs;

				// Compose all predicates once per selector run so legs are iterated a single time.
				const predicates: ((leg: DispatchLeg) => boolean)[] = [
					(leg) => getEnumValueByKey(LegStatus, leg.legStatus) !== LegStatus.DL
				];
				if (filters) {
					for (const filter of filters) {
						const filterMap = dispatchFilterMaps[filter.map];
						const op = operatorMaps[filter.operator];
						predicates.push((leg) => op(leg, filterMap.fields, filterMap.fieldType, filter.value, filterMap.fieldProperty));
					}
				}

				const filteredLegs = legs.filter((leg) => {
					for (const predicate of predicates) {
						if (!predicate(leg)) return false;
					}
					return true;
				});

				// filter() already returned a fresh array, so sortDispatchLegs may mutate it safely.
				return sortDispatchLegs(filteredLegs);
			}
		);
	}, []);

	/** Local RTK Selector to select and sort the Dispatch Legs down by the Job */
	const selectJobLegs = useMemo(() => {
		const noLegs: DispatchLeg[] = [];

		return createSelector(
			(legs: DispatchLeg[] | null | undefined) => legs,
			(_: DispatchLeg[] | null | undefined, consignment: string | undefined) => consignment,
			(legs, consignment) => {
				if (!legs?.length) return noLegs;
				const csnNum = consignment !== undefined ? Number(consignment) : undefined;
				const filteredLegs = csnNum === undefined ? legs.slice() : legs.filter((leg) => leg.csnNo === csnNum);
				return sortJobLegs(filteredLegs);
			}
		);
	}, []);

	/** Fetch the Dispatch Legs */
	const { dispatchLegs, isLoading: fetchDispatchLoading } = useFetchDispatchLegsQuery(String(selectedTerminal?.number), {
		pollingInterval: csnId || jobId ? 0 : 120000, // Refresh every 2 minutes if mode is dispatch
		selectFromResult: ({ data, isLoading }) => ({
			isLoading,
			dispatchLegs: selectFilteredLegs(data, currentQuery.configuration.filters)
		})
	});

	const { jobLegs, isFetching: fetchJobLegsLoading } = useFetchDispatchLegsByJobIdQuery(
		{
			terminalNumber: String(selectedTerminal?.number),
			jobId: String(jobId)
		},
		{
			skip: !jobId,
			pollingInterval: csnId || jobId ? 120000 : 0, // Refresh every 2 minutes if mode is not dispatch
			/**
			 * Using currentData here because of the nature of this use case.
			 * When the parameters change we would prefer showing a loader as opposed
			 * to 'data' which would contain the previously retreived legs.
			 */
			selectFromResult: ({ currentData, ...jobApi }) => ({
				...jobApi,
				jobLegs: selectJobLegs(currentData, csnId)
			})
		}
	);

	return {
		mode: csnId ? 'consignment' : jobId ? 'job' : 'dispatch',
		dispatchLegs: jobId ? jobLegs : dispatchLegs,
		isLoading: jobId || csnId ? fetchJobLegsLoading : fetchDispatchLoading
	};
}
